import { useId, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateLearnerIdentity, type LearnerIdentityErrors } from "@/lib/learners/identity";

type Props = {
  userId: string;
  initialDisplayName?: string | null;
  initialUsername?: string | null;
  onSaved: () => void;
};

/** Canonical learner identity lives on profiles (display_name + unique username). */
const LearnerIdentityForm = ({ userId, initialDisplayName, initialUsername, onSaved }: Props) => {
  const [displayName, setDisplayName] = useState(initialDisplayName ?? "");
  const [username, setUsername] = useState(initialUsername ?? "");
  const [errors, setErrors] = useState<LearnerIdentityErrors>({});
  const [saving, setSaving] = useState(false);
  const nameId = useId();
  const userIdField = useId();
  const nameErr = `${nameId}-err`;
  const userErr = `${userIdField}-err`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateLearnerIdentity({ displayName, username });
    setErrors(v);
    if (v.displayName || v.username) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim(), username: username.trim() })
      .eq("id", userId);
    setSaving(false);
    if (error) {
      setErrors(error.code === "23505"
        ? { username: "That username is taken. Try another." }
        : { username: "We couldn't save that. Please try again." });
      return;
    }
    onSaved();
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="space-y-1">
        <Label htmlFor={nameId}>Display name</Label>
        <Input
          id={nameId}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          aria-invalid={!!errors.displayName}
          aria-describedby={errors.displayName ? nameErr : undefined}
          required
        />
        {errors.displayName && <p id={nameErr} role="alert" className="text-sm text-destructive">{errors.displayName}</p>}
      </div>
      <div className="space-y-1">
        <Label htmlFor={userIdField}>Username</Label>
        <Input
          id={userIdField}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          aria-invalid={!!errors.username}
          aria-describedby={errors.username ? userErr : undefined}
          autoComplete="username"
          required
        />
        {errors.username && <p id={userErr} role="alert" className="text-sm text-destructive">{errors.username}</p>}
      </div>
      <Button type="submit" className="w-full" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
    </form>
  );
};

export default LearnerIdentityForm;
